/**
 * Price History Filter component for date range, gold type, and branch selection
 * Using shadcn/ui components
 */

import { useState, useEffect } from 'react'
import { useGoldPrices } from '@/services/api/goldPrice'
import { getFirstDayOfMonth, getLastDayOfMonth } from '@/utils/dateUtils'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { DatePicker } from '@/components/common/DatePicker'

interface PriceHistoryFilterProps {
  onFilterChange: (filters: {
    startDate?: string
    endDate?: string
    goldTypeId?: number
    branchName?: string
  }) => void
}

export function PriceHistoryFilter({ onFilterChange }: PriceHistoryFilterProps) {
  const { data: goldTypes } = useGoldPrices('Hồ Chí Minh')
  const [filters, setFilters] = useState<{
    startDate: string
    endDate: string
    goldTypeId: number | ''
    branchName: string
  }>({
    startDate: getFirstDayOfMonth(),
    endDate: getLastDayOfMonth(),
    goldTypeId: '',
    branchName: 'Hồ Chí Minh',
  })

  useEffect(() => {
    const filterParams: {
      startDate?: string
      endDate?: string
      goldTypeId?: number
      branchName?: string
    } = {}
    if (filters.startDate) filterParams.startDate = filters.startDate
    if (filters.endDate) filterParams.endDate = filters.endDate
    if (filters.goldTypeId) filterParams.goldTypeId = Number(filters.goldTypeId)
    if (filters.branchName) filterParams.branchName = filters.branchName
    onFilterChange(filterParams)
  }, [filters, onFilterChange])

  const handleReset = () => {
    setFilters({
      startDate: getFirstDayOfMonth(),
      endDate: getLastDayOfMonth(),
      goldTypeId: '',
      branchName: 'Hồ Chí Minh',
    })
  }

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>Bộ lọc</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <Label htmlFor="startDate" className="mb-2">
              Từ ngày
            </Label>
            <DatePicker
              id="startDate"
              value={filters.startDate}
              onChange={(value) => setFilters({ ...filters, startDate: value })}
              placeholder="Chọn ngày bắt đầu"
            />
          </div>

          <div>
            <Label htmlFor="endDate" className="mb-2">
              Đến ngày
            </Label>
            <DatePicker
              id="endDate"
              value={filters.endDate}
              onChange={(value) => setFilters({ ...filters, endDate: value })}
              placeholder="Chọn ngày kết thúc"
            />
          </div>

          <div>
            <Label htmlFor="goldTypeId" className="mb-2">
              Loại vàng
            </Label>
            <Select
              value={filters.goldTypeId === '' ? 'all' : String(filters.goldTypeId)}
              onValueChange={(value) =>
                setFilters({ ...filters, goldTypeId: value === 'all' ? '' : Number(value) })
              }
            >
              <SelectTrigger id="goldTypeId" className="min-h-[44px]">
                <SelectValue placeholder="Tất cả" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tất cả</SelectItem>
                {goldTypes?.map((type) => (
                  <SelectItem key={type.Id} value={String(type.Id)}>
                    {type.TypeName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="branchName" className="mb-2">
              Chi nhánh
            </Label>
            <Select
              value={filters.branchName}
              onValueChange={(value) => setFilters({ ...filters, branchName: value })}
            >
              <SelectTrigger id="branchName" className="min-h-[44px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Hồ Chí Minh">Hồ Chí Minh</SelectItem>
                <SelectItem value="Miền Bắc">Miền Bắc</SelectItem>
                <SelectItem value="Miền Trung">Miền Trung</SelectItem>
                <SelectItem value="Miền Tây">Miền Tây</SelectItem>
                <SelectItem value="Hạ Long">Hạ Long</SelectItem>
                <SelectItem value="Hải Phòng">Hải Phòng</SelectItem>
                <SelectItem value="Huế">Huế</SelectItem>
                <SelectItem value="Quảng Ngãi">Quảng Ngãi</SelectItem>
                <SelectItem value="Nha Trang">Nha Trang</SelectItem>
                <SelectItem value="Biên Hòa">Biên Hòa</SelectItem>
                <SelectItem value="Bạc Liêu">Bạc Liêu</SelectItem>
                <SelectItem value="Cà Mau">Cà Mau</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <Button onClick={handleReset} variant="outline" className="w-full sm:w-auto h-11 min-h-[44px]">
            Đặt lại
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

